const BACKOFFICE_API_URL =
  process.env.REACT_APP_BACKOFFICE_API_URL || "http://localhost:3500";

/**
 * Resuelve el token cifrado del enlace de la encuesta 4C llamando al backoffice.
 * El token viene cifrado (AES-256-GCM) desde el correo.
 *
 * @param {string} token - Token tal cual en la URL (?token=...)
 * @returns {Promise<{ id_usuario: number, id_equipo: number | null, id_encuesta: number } | null>}
 */
export async function resolveSurveyToken(token) {
  if (!token || typeof token !== "string") {
    return null;
  }
  try {
    const url = `${BACKOFFICE_API_URL.replace(/\/$/, "")}/gestionEncuestas/resolveToken`;
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token: token.trim() }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    const id_usuario = data.id_usuario != null ? Number(data.id_usuario) : null;
    const id_encuesta = data.id_encuesta != null ? Number(data.id_encuesta) : null;
    const id_equipo = data.id_equipo != null ? Number(data.id_equipo) : null;
    if (id_usuario == null || id_encuesta == null || Number.isNaN(id_usuario) || Number.isNaN(id_encuesta)) {
      return null;
    }
    const nombre_usuario = data.nombre_usuario != null ? String(data.nombre_usuario).trim() : null;
    const nombre_equipo = data.nombre_equipo != null ? String(data.nombre_equipo).trim() : null;
    const tipo = data.tipo != null ? String(data.tipo).trim() : null;
    return { id_usuario, id_encuesta, id_equipo, nombre_usuario, nombre_equipo, tipo };
  } catch {
    return null;
  }
}

/**
 * Obtiene las preguntas del test desde el backoffice (BD 4creatividades).
 * @returns {Promise<Array<{ id_pregunta: number, texto_es: string, texto_en?: string }>>}
 */
export async function fetchPreguntas() {
  try {
    const url = `${BACKOFFICE_API_URL.replace(/\/$/, "")}/gestionEncuestas/preguntas`;
    const res = await fetch(url);
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

/**
 * Envía una o más respuestas del test 4C al backoffice (cada una se guarda al instante).
 * @param {{ id_usuario: number, id_encuesta: number }} ids
 * @param {{ id_pregunta: number, valor: number }[]} respuestas
 * @returns {Promise<boolean>}
 */
export async function guardarRespuestas(ids, respuestas) {
  if (!ids?.id_usuario || !ids?.id_encuesta || !Array.isArray(respuestas) || respuestas.length === 0) {
    return { ok: false, completado: false };
  }
  try {
    const url = `${BACKOFFICE_API_URL.replace(/\/$/, "")}/gestionEncuestas/guardarRespuestas`;
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        id_usuario: ids.id_usuario,
        id_encuesta: ids.id_encuesta,
        respuestas: respuestas.map((r) => ({ id_pregunta: r.id_pregunta, valor: Number(r.valor) })),
      }),
    });
    if (!res.ok) return { ok: false, completado: false };
    const data = await res.json().catch(() => ({}));
    return { ok: true, completado: Boolean(data.completado) };
  } catch {
    return { ok: false, completado: false };
  }
}

/**
 * Obtiene las respuestas ya guardadas para este usuario y encuesta (reanudar).
 * @param {{ id_usuario: number, id_encuesta: number }} ids
 * @returns {Promise<Array<{ id_pregunta: number, valor: number }>>}
 */
export async function getRespuestas(ids) {
  if (!ids?.id_usuario || !ids?.id_encuesta) return [];
  try {
    const url = `${BACKOFFICE_API_URL.replace(/\/$/, "")}/gestionEncuestas/getRespuestas`;
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        id_usuario: ids.id_usuario,
        id_encuesta: ids.id_encuesta,
      }),
    });
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

export async function fetchPerfil(ids) {
  if (!ids?.id_usuario || !ids?.id_encuesta) return null;
  try {
    const url = `${BACKOFFICE_API_URL.replace(/\/$/, "")}/gestionEncuestas/getPerfil`;
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        id_usuario: ids.id_usuario,
        id_encuesta: ids.id_encuesta,
      }),
    });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

/**
 * Decodifica el token en base64 (formato legacy "id_usuario:id_equipo:id_encuesta").
 * Para enlaces con token cifrado usar resolveSurveyToken().
 *
 * @param {string} token - Token en base64 (ej. desde ?token=...)
 * @returns {{ id_usuario: number, id_equipo: number | null, id_encuesta: number } | null}
 */
export function decodeSurveyToken(token) {
  if (!token || typeof token !== "string") {
    return null;
  }
  try {
    const decoded = atob(token.trim());
    const parts = decoded.split(":");
    if (parts.length !== 3) {
      return null;
    }
    const [id_usuario, id_equipo, id_encuesta] = parts.map((p) => parseInt(p, 10));
    if (
      Number.isNaN(id_usuario) ||
      Number.isNaN(id_encuesta)
    ) {
      return null;
    }
    return {
      id_usuario,
      id_encuesta,
      id_equipo: Number.isNaN(id_equipo) ? null : id_equipo,
    };
  } catch {
    return null;
  }
}
